import { useEffect, useRef, useState, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import Layout from 'containers/Layout'
import LoadingIcon from 'components/atoms/LoadingIcon'
import { Formik, Form } from 'formik'
import _, { get } from 'lodash'
import apiGet from 'lib/network/apiGet'
import notify from 'components/atoms/Notify'
import apiPost from 'lib/network/apiPost'
import LocationPicker from './modals/googleMaps'
import BookingSuccessModal from './modals/BookingSuccessModal'
import DatePicker, { registerLocale } from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { arSA, enUS } from 'date-fns/locale'
import * as Yup from 'yup'
import useModal from 'hooks/useModal'
import { useTranslation } from 'react-i18next'
import { BOOKING_BTN_GHOST, BOOKING_PAGE_CANVAS, BOOKING_PANEL } from './bookingUiTokens'
import CreateBookingStepper from './create/CreateBookingStepper'
import CreateBookingWizardFooter from './create/CreateBookingWizardFooter'
import LocationStep from './create/steps/LocationStep'
import CarsStep from './create/steps/CarsStep'
import ReviewStep from './create/steps/ReviewStep'
import { isValidBookingPhone, normalizePhone } from 'utils/phone'
import { getDifferenceInDays } from 'helpers'
import { isPastBookingDate } from './create/bookingDateUtils'

registerLocale('ar', arSA)
registerLocale('en', enUS)

const BookingProfile = ({ readOnly, loading, isEdit }) => {
  const { t, i18n } = useTranslation()

  const userFromStore = useSelector(state => get(state, 'user.user'))
  // eslint-disable-next-line
  const hasAdminAccess = userFromStore?.roles?.some(role => role.type === 'admin')
  // eslint-disable-next-line
  const isCompanyRole = userFromStore?.roles?.some(role => role.type === 'company')
  const isCompanyBookingFlow = Boolean(isCompanyRole && !hasAdminAccess)
  const isSuperCompanyUser = Boolean(userFromStore?.superCompanyUser)

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        rentalStartDate: Yup.mixed()
          .nullable()
          .test('is-date', 'Rental start date is required', function (value) {
            if (_.isNil(value) || value === '') {
              return false
            }
            // eslint-disable-next-line lodash/prefer-lodash-typecheck
            if (value instanceof Date && !isNaN(value.getTime())) {
              return true
            }
            return false
          })
          .test('not-in-past', t('bookingProfile.validation.startNotInPast'), function (value) {
            if (!isCompanyBookingFlow) return true
            return !isPastBookingDate(value)
          }),
        rentalEndDate: Yup.mixed()
          .nullable()
          .test('is-date', 'Rental end date is required', function (value) {
            if (_.isNil(value) || value === '') {
              return false
            }
            // eslint-disable-next-line lodash/prefer-lodash-typecheck
            if (value instanceof Date && !isNaN(value.getTime())) {
              return true
            }
            return false
          })
          .test('is-after-start', t('bookingProfile.validation.rentalEndDateMustBeLater'), function (value) {
            const { rentalStartDate } = this.parent
            if (!value || !rentalStartDate) return true
            return new Date(value).getTime() > new Date(rentalStartDate).getTime()
          })
          .test('not-in-past', t('bookingProfile.validation.endNotInPast'), function (value) {
            if (!isCompanyBookingFlow) return true
            return !isPastBookingDate(value)
          }),
        deliveryMethod: Yup.string()
          .required(t('bookingProfile.validation.deliveryMethodRequired'))
          .oneOf(['deliver', 'pickup'], t('bookingProfile.validation.deliveryMethodRequired')),
        lat: Yup.string().when('deliveryMethod', {
          is: 'pickup',
          then: schema => schema.transform(value => (_.isNil(value) ? value : _.trim(String(value)))).required(t('bookingProfile.validation.locationRequired')),
          otherwise: schema => schema.notRequired(),
        }),
        lon: Yup.string().when('deliveryMethod', {
          is: 'pickup',
          then: schema => schema.transform(value => (_.isNil(value) ? value : _.trim(String(value)))).required(t('bookingProfile.validation.locationRequired')),
          otherwise: schema => schema.notRequired(),
        }),
        pickupLat: Yup.string().when('deliveryMethod', {
          is: 'deliver',
          then: schema => schema.transform(value => (_.isNil(value) ? value : _.trim(String(value)))).required(t('bookingProfile.validation.pickupLocationRequired')),
          otherwise: schema => schema.notRequired(),
        }),
        pickupLon: Yup.string().when('deliveryMethod', {
          is: 'deliver',
          then: schema => schema.transform(value => (_.isNil(value) ? value : _.trim(String(value)))).required(t('bookingProfile.validation.pickupLocationRequired')),
          otherwise: schema => schema.notRequired(),
        }),
        dropoffLat: Yup.string().when('deliveryMethod', {
          is: 'deliver',
          then: schema => schema.transform(value => (_.isNil(value) ? value : _.trim(String(value)))).required(t('bookingProfile.validation.dropoffLocationRequired')),
          otherwise: schema => schema.notRequired(),
        }),
        dropoffLon: Yup.string().when('deliveryMethod', {
          is: 'deliver',
          then: schema => schema.transform(value => (_.isNil(value) ? value : _.trim(String(value)))).required(t('bookingProfile.validation.dropoffLocationRequired')),
          otherwise: schema => schema.notRequired(),
        }),
        ...(isCompanyBookingFlow
          ? {
              paymentStatus: Yup.string()
                .required(t('bookingProfile.validation.paymentStatusRequired'))
                .oneOf(['paid', 'unpaid'], t('bookingProfile.validation.invalidPaymentStatus')),
              paymentMethod: Yup.string()
                .required(t('bookingProfile.validation.paymentMethodRequired'))
                .oneOf(['pos', 'transfer'], t('bookingProfile.validation.invalidPaymentMethod')),
              transferReferenceNumber: Yup.string().when('paymentMethod', {
                is: 'transfer',
                // eslint-disable-next-line
                then: schema => schema.trim().required(t('bookingProfile.validation.transferReferenceRequired')),
                otherwise: schema => schema.notRequired(),
              }),
              transferReceiptFileName: Yup.string().when('paymentMethod', {
                is: 'transfer',
                // eslint-disable-next-line
                then: schema => schema.trim().required(t('bookingProfile.validation.transferReceiptRequired')),
                otherwise: schema => schema.notRequired(),
              }),
            }
          : {
              paymentMethod: Yup.string()
                .required('Payment method is required')
                .oneOf(['checkout', 'transfer', 'direct_link'], t('bookingProfile.validation.invalidPaymentMethod')),
              status: Yup.string().when('paymentMethod', {
                is: 'direct_link',
                // eslint-disable-next-line
                then: schema =>
                  schema
                    .required(t('bookingProfile.validation.statusRequired'))
                    .oneOf(['confirmed', 'pending', 'userReview', 'inProgress', 'completed'], t('bookingProfile.validation.invalidStatus')),
                otherwise: schema => schema.notRequired(),
              }),
              paymentStatus: Yup.string().when('paymentMethod', {
                is: 'direct_link',
                // eslint-disable-next-line
                then: schema =>
                  schema.required(t('bookingProfile.validation.paymentStatusRequired')).oneOf(['paid', 'unpaid'], t('bookingProfile.validation.invalidPaymentStatus')),
                otherwise: schema => schema.notRequired(),
              }),
              transferReferenceNumber: Yup.string().when('paymentMethod', {
                is: 'transfer',
                // eslint-disable-next-line
                then: schema => schema.trim().required(t('bookingProfile.validation.transferReferenceRequired')),
                otherwise: schema => schema.notRequired(),
              }),
              transferReceiptFileName: Yup.string().when('paymentMethod', {
                is: 'transfer',
                // eslint-disable-next-line
                then: schema => schema.trim().required(t('bookingProfile.validation.transferReceiptRequired')),
                otherwise: schema => schema.notRequired(),
              }),
            }),
      }),
    [t, isCompanyBookingFlow]
  )
  const isArabic = i18n.language === 'ar'
  const datePickerLocale = isArabic ? 'ar' : 'en'

  const [companyBranches, setCompanyBranches] = useState([])
  const [branchesLoading, setBranchesLoading] = useState(false)
  const [companyBranchFetchKey, setCompanyBranchFetchKey] = useState(0)
  const [deliveryMethodFetchKey, setDeliveryMethodFetchKey] = useState(0)

  useEffect(() => {
    if (!isCompanyBookingFlow || !isSuperCompanyUser) return undefined
    let cancelled = false
    const loadBranches = async () => {
      setBranchesLoading(true)
      try {
        const response = await apiGet('branches', { pageNumber: 1, pageSize: 500 })
        if (cancelled) return
        const list = response?.data?.branch || []
        // eslint-disable-next-line
        setCompanyBranches(Array.isArray(list) ? list : [])
      } catch (e) {
        if (!cancelled) setCompanyBranches([])
      } finally {
        if (!cancelled) setBranchesLoading(false)
      }
    }
    loadBranches()
    return () => {
      cancelled = true
    }
  }, [isCompanyBookingFlow, isSuperCompanyUser])

  const companyBranchOptions = useMemo(
    () => [{ id: 'all', name: t('bookingProfile.labels.allBranches') }, ...companyBranches.map(b => ({ id: b._id, name: b.name }))],
    [companyBranches, t]
  )

  const bookingInitialValues = useMemo(
    () => ({
      selectedUser: '',
      selectedCar: '',
      selectedBranchId: isCompanyBookingFlow && !isSuperCompanyUser ? '' : 'all',
      phone: '',
      lat: '',
      lon: '',
      rentalStartDate: '',
      rentalEndDate: '',
      deliveryMethod: 'deliver',
      address: '',
      pickupLocation: '',
      dropoffLocation: '',
      pickupLat: '',
      pickupLon: '',
      dropoffLat: '',
      dropoffLon: '',
      promoCode: '',
      status: '',
      paymentStatus: '',
      paymentMethod: '',
      transferReferenceNumber: '',
      transferReceiptFileName: '',
      insurance: false,
      extras: [],
      sentPaymentEmail: false,
    }),
    [isCompanyBookingFlow, isSuperCompanyUser]
  )

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isResolvingPhone, setIsResolvingPhone] = useState(false)
  const [transferReceiptFile, setTransferReceiptFile] = useState(null)
  const selectDateTimePlaceholder = t('common.placeholders.selectDateTime')
  const searchOptionsPlaceholder = t('bookingProfile.placeholders.searchOptions')
  const clickToPickLocationPlaceholder = t('bookingProfile.placeholders.clickToPickLocation')
  const clickToPickPickupPlaceholder = t('bookingProfile.placeholders.clickToPickPickupLocation')
  const clickToPickDropoffPlaceholder = t('bookingProfile.placeholders.clickToPickDropoffLocation')
  const [activeStep, setActiveStep] = useState(1)
  const [userOptions, setUserOptions] = useState([])
  const [loadingCarId, setLoadingCarId] = useState(null)
  const [selectedCarId, setSelectedCarId] = useState(null)
  const [selectedCar, setSelectedCar] = useState(null)
  const [deliveryCreated, setDeliveryCreated] = useState(false)
  const [deliveryData, setDeliveryData] = useState(null)
  const [isDeliveryPricingLoading, setIsDeliveryPricingLoading] = useState(false)
  const [pricingError, setPricingError] = useState(false)
  const [paymentStatus, setPaymentStatus] = useState(null)
  const [paymentUrl, setPaymentUrl] = useState(null)
  const [showMap, setShowMap] = useState(false)
  const [showPickupMap, setShowPickupMap] = useState(false)
  const [showDropoffMap, setShowDropoffMap] = useState(false)
  const [carDetail, setCarDetail] = useState()
  const [phoneUserId, setPhoneUserId] = useState(null)
  const [bookingModelYear, setBookingModelYear] = useState(null)
  const [bookingMake, setBookingMake] = useState(null)
  const [bookingModel, setBookingModel] = useState(null)
  const [bookingId, setBookingId] = useState(null)
  const [bookingToken, setBookingToken] = useState(null)
  const [bookingPaymentLink, setBookingPaymentLink] = useState(null)
  const [shouldRedirectAfterModal, setShouldRedirectAfterModal] = useState(false)
  const formikRef = useRef(null)

  const [carData, setCarData] = useState([])
  const [isLoadingCars, setIsLoadingCars] = useState(false)
  const [search, setSearch] = useState('')
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPage: 1,
    pageSize: 6,
  })

  const handleCompanyBranchChange = (branchId, setFieldValue) => {
    // eslint-disable-next-line
    const normalizedBranchId = branchId != null && branchId !== '' ? String(branchId).replace(/\?+$/, '').trim() : 'all'
    setFieldValue('selectedBranchId', normalizedBranchId, false)
    setFieldValue('selectedCar', '', false)
    setSelectedCarId(null)
    setSelectedCar(null)
    setCompanyBranchFetchKey(k => k + 1)
  }

  const history = useHistory()
  const location = useLocation()

  const formatDateForAPI = dateObject => {
    if (!dateObject) return null
    const date = new Date(dateObject)

    const year = date.getFullYear()
    const month = _.padStart(String(date.getMonth() + 1), 2, '0')
    const day = _.padStart(String(date.getDate()), 2, '0')
    const hours = _.padStart(String(date.getHours()), 2, '0')
    const minutes = _.padStart(String(date.getMinutes()), 2, '0')

    return `${year}-${month}-${day} ${hours}:${minutes}:00.000`
  }

  const getSteps = () => [
    { id: 1, stepKey: 'location' },
    { id: 2, stepKey: 'cars' },
    { id: 3, stepKey: 'detail' },
  ]

  const getListingCoordinates = values => {
    const isDeliver = values.deliveryMethod === 'deliver'
    return {
      lat: isDeliver ? values.pickupLat || values.lat : values.lat,
      lon: isDeliver ? values.pickupLon || values.lon : values.lon,
    }
  }

  const STEP1_VALIDATION_FIELDS = ['rentalStartDate', 'rentalEndDate', 'deliveryMethod', 'lat', 'lon', 'pickupLat', 'pickupLon', 'dropoffLat', 'dropoffLon', 'phone']

  const getStep1ValidationErrors = allErrors => _.pick(allErrors, STEP1_VALIDATION_FIELDS)

  const handleDeliveryMethodChange = (method, setFieldValue) => {
    setFieldValue('deliveryMethod', method, false)
    setFieldValue('selectedCar', '', false)
    setSelectedCarId(null)
    setSelectedCar(null)
    setCarData([])
    setDeliveryCreated(false)
    setDeliveryData(null)
    setDeliveryMethodFetchKey(k => k + 1)
    if (activeStep === 2) {
      setCompanyBranchFetchKey(k => k + 1)
    }
  }

  useEffect(() => {
    if (hasAdminAccess) {
      fetchUsers()
    }
    return () => {
      setDeliveryCreated(false)
      setDeliveryData(null)
    }
  }, [hasAdminAccess])

  useEffect(() => {
    if (selectedCarId) {
      setDeliveryCreated(false)
      setDeliveryData(null)
    }
  }, [selectedCarId])

  const fetchCars = async (page, values) => {
    const { rentalStartDate, rentalEndDate, selectedBranchId, deliveryMethod } = values
    const { lat, lon } = getListingCoordinates(values)

    if (!lat || !lon || !rentalStartDate || !rentalEndDate) {
      notify.error(t('bookingProfile.messages.completeLocationFirst'))
      setActiveStep(1)
      return false
    }

    try {
      setIsLoadingCars(true)
      if (page === 1) {
        setCarData([])
      }

      const deliveryOnly = deliveryMethod === 'deliver' ? 'true' : 'false'

      if (isCompanyBookingFlow) {
        const params = {
          'booking[from]': formatDateForAPI(rentalStartDate),
          'booking[to]': formatDateForAPI(rentalEndDate),
          'location[lat]': lat,
          'location[lon]': lon,
          deliveryOnly,
          pageNumber: page,
          pageSize: pagination.pageSize,
        }
        if (isSuperCompanyUser) {
          // eslint-disable-next-line
          const rawBranchId = selectedBranchId && selectedBranchId !== '' ? String(selectedBranchId).replace(/\?+$/, '').trim() : 'all'
          params.branchId = rawBranchId || 'all'
        }
        if (search && _.trim(search)) {
          params.query = _.trim(search)
        }
        const queryString = new URLSearchParams(params).toString()
        const response = await apiGet(`company/cars/for-bookings?${queryString}`)

        if (response.success || response.code === 200) {
          const { rentalCars, pagination: newPagination } = response.data
          setCarData(prevCars => [...prevCars, ...rentalCars])
          setPagination({
            currentPage: page,
            totalPage: newPagination.totalPage,
            pageSize: newPagination.pageSize,
          })
          return true
        }
      } else {
        const params = {
          'booking[from]': formatDateForAPI(rentalStartDate),
          'booking[to]': formatDateForAPI(rentalEndDate),
          'location[lat]': lat,
          'location[lon]': lon,
          deliveryOnly,
          pageNumber: page,
          pageSize: pagination.pageSize,
        }
        if (search && _.trim(search)) {
          params.query = _.trim(search)
        }

        const queryString = new URLSearchParams(params).toString()
        const response = await apiGet(`cars/for-bookings?${queryString}`)

        if (response.success || response.code === 200) {
          const { rentalCars, pagination: newPagination } = response.data
          setCarData(prevCars => [...prevCars, ...rentalCars])
          setPagination({
            currentPage: page,
            totalPage: newPagination.totalPage,
            pageSize: newPagination.pageSize,
          })
          return true
        }
      }
    } catch (error) {
      notify.error(t('bookingProfile.messages.fetchCarsFailed'))
      return false
    } finally {
      setIsLoadingCars(false)
    }
    return false
  }

  useEffect(() => {
    if (activeStep === 2) {
      const fetchCarsWithSearch = async () => {
        const formValues = formikRef.current?.values
        if (formValues) {
          await fetchCars(1, formValues)
        }
      }
      fetchCarsWithSearch()
    }
  }, [activeStep, search, companyBranchFetchKey, deliveryMethodFetchKey, isSuperCompanyUser])

  const loadMore = () => {
    if (pagination.currentPage < pagination.totalPage) {
      const formValues = formikRef.current?.values
      if (formValues) {
        fetchCars(pagination.currentPage + 1, formValues)
      }
    }
  }
  const handleSearchChange = e => {
    const value = e.target.value
    setSearch(value)
  }

  const fetchUsers = async (searchQuery = '') => {
    const response = await apiGet('users', { query: searchQuery })

    if (response?.data) {
      const users = response.data.user.map(user => ({
        id: user._id,
        name: user.fullName || user.email || user.phone,
      }))
      setUserOptions(users)
    } else {
      setUserOptions([])
    }
  }

  useEffect(() => {
    if (activeStep !== 3 || !selectedCarId) return undefined

    let cancelled = false
    const formValues = formikRef.current?.values
    if (!formValues) return undefined

    const loadReviewPricing = async () => {
      setIsDeliveryPricingLoading(true)
      setPricingError(false)
      try {
        const carLoaded = await fetchCar(selectedCarId, formValues)
        if (cancelled) return

        let deliveryLoaded = true
        if (!deliveryCreated) {
          deliveryLoaded = await createDelivery(formValues)
        }
        if (cancelled) return

        // Either call failing leaves the summary without a real price, so flag it instead of
        // letting every amount fall back to 0.
        setPricingError(!carLoaded || !deliveryLoaded)
      } finally {
        setIsDeliveryPricingLoading(false)
      }
    }

    loadReviewPricing()

    return () => {
      cancelled = true
    }
  }, [activeStep, selectedCarId, isCompanyBookingFlow, deliveryCreated, phoneUserId])

  const fetchCar = async (id, values) => {
    const { lat, lon, rentalStartDate, rentalEndDate, selectedUser } = values
    try {
      if (isCompanyBookingFlow) {
        const params = new URLSearchParams({
          bookingFrom: formatDateForAPI(rentalStartDate),
          bookingTo: formatDateForAPI(rentalEndDate),
        })
        const customerId = phoneUserId || selectedUser
        if (customerId) {
          params.set('customerUserId', customerId)
        }
        const response = await apiGet(`company/cars/${id}?${params.toString()}`)
        const data = response.data
        setCarDetail(data)
        return Boolean(data)
      } else {
        const params = {
          bookingFrom: formatDateForAPI(rentalStartDate),
          bookingTo: formatDateForAPI(rentalEndDate),

          lat,
          lon,
        }

        const queryString = new URLSearchParams(params).toString()

        const response = await apiGet(`booking/car/${id}?${queryString}`)
        const data = response.data
        setCarDetail(data)
        return Boolean(data)
      }
    } catch (error) {
      notify.error(t('bookingProfile.messages.fetchCarFailed'))
      setCarDetail(undefined)
      return false
    }
  }

  const handleNext = async values => {
    const currentSteps = getSteps()
    // eslint-disable-next-line
    const currentStepIndex = currentSteps.findIndex(step => step.id === activeStep)

    if (currentStepIndex < currentSteps.length - 1) {
      const nextStep = currentSteps[currentStepIndex + 1].id

      if (nextStep === 2) {
        const form = formikRef.current
        if (!form) return

        const fieldsToTouch = ['rentalStartDate', 'rentalEndDate', 'phone', 'deliveryMethod', 'lat', 'lon', 'pickupLat', 'pickupLon', 'dropoffLat', 'dropoffLon']
        _.forEach(fieldsToTouch, field => form.setFieldTouched(field, true, false))

        const allValidationErrors = await form.validateForm()
        const validationErrors = getStep1ValidationErrors(allValidationErrors)
        if (!_.isEmpty(validationErrors)) {
          if (validationErrors.phone) {
            notify.error(validationErrors.phone)
          } else if (validationErrors.deliveryMethod) {
            notify.error(validationErrors.deliveryMethod)
          } else if (validationErrors.rentalEndDate) {
            notify.error(validationErrors.rentalEndDate)
          } else if (validationErrors.lat || validationErrors.pickupLat || validationErrors.dropoffLat || validationErrors.rentalStartDate) {
            notify.error(t('bookingProfile.messages.completeLocationFirst'))
          }
          return
        }

        const { lat: listLat, lon: listLon } = getListingCoordinates(values)
        const hasDeliverLocations = values.deliveryMethod === 'deliver' && values.pickupLat && values.pickupLon && values.dropoffLat && values.dropoffLon
        const hasPickupLocation = values.deliveryMethod === 'pickup' && values.lat && values.lon
        const hasRequiredFields = values.rentalStartDate && values.rentalEndDate && listLat && listLon && (hasDeliverLocations || hasPickupLocation)

        if (!hasRequiredFields) {
          notify.error(t('bookingProfile.messages.completeLocationFirst'))
          return
        }

        const phoneRequired = isCompanyBookingFlow || !values.selectedUser
        if (phoneRequired && (!values.phone || !isValidBookingPhone(values.phone))) {
          notify.error(t('bookingProfile.validation.phoneInvalid'))
          return
        }

        if (!isCompanyBookingFlow && !values.selectedUser && !values.phone) {
          notify.error(t('bookingProfile.messages.selectUserOrPhone'))
          return
        }

        if (values.deliveryMethod === 'deliver') {
          form.setFieldValue('lat', values.pickupLat, false)
          form.setFieldValue('lon', values.pickupLon, false)
          form.setFieldValue('address', values.pickupLocation || values.address, false)
        }

        if (values.phone) {
          try {
            setIsResolvingPhone(true)
            const phoneUserResponse = await apiPost('phone/user', { phone: normalizePhone(values.phone) })

            if (phoneUserResponse.success || phoneUserResponse.code === 200) {
              const { userId } = phoneUserResponse.data

              setPhoneUserId(userId)

              if (!values.selectedUser) {
                formikRef.current.setFieldValue('selectedUser', userId)
              }

              setActiveStep(nextStep)
            } else {
              notify.error(phoneUserResponse.message || t('bookingProfile.messages.phoneFailed'))
            }
          } catch (error) {
            notify.error(t('bookingProfile.messages.phoneError'))
          } finally {
            setIsResolvingPhone(false)
          }
        } else if (values.selectedUser) {
          setPhoneUserId(null)
          setActiveStep(nextStep)
        } else {
          setActiveStep(nextStep)
        }
      } else if (nextStep === 3) {
        if (!values.selectedCar) {
          notify.error(t('bookingProfile.messages.selectCarFirst'))
          return
        }
        setActiveStep(nextStep)
      } else {
        setActiveStep(nextStep)
      }
    }
  }

  const handlePrevious = () => {
    if (activeStep > 1) {
      setActiveStep(activeStep - 1)
    }
  }

  const handleSelectCar = (carId, setFieldValue) => {
    setLoadingCarId(carId)
    setSelectedCarId(carId)

    setDeliveryCreated(false)
    setDeliveryData(null)

    // eslint-disable-next-line
    const car = carData.find(c => c._id === carId)
    setSelectedCar(car)

    setFieldValue('selectedCar', carId)
    setFieldValue('extras', [], false)
    setLoadingCarId(null)

    // Cars step: advance immediately after choosing a car (no separate Next tap).
    if (activeStep === 2) {
      setActiveStep(3)
    }
  }
  const createDelivery = async values => {
    if (deliveryCreated) {
      return true
    }

    const userIdToUse = phoneUserId || values.selectedUser
    if (!userIdToUse) {
      notify.error(t('bookingProfile.messages.selectUserOrPhone'))
      return false
    }

    try {
      setIsSubmitting(true)

      const deliveryType = values.deliveryMethod === 'deliver' ? 'DELIVER_TO_ME' : 'PICK_UP_MYSELF'

      const requestData = {
        rentalCarId: values.selectedCar,
        userId: userIdToUse,
        type: deliveryType,
        booking: {
          from: formatDateForAPI(values.rentalStartDate),
          to: formatDateForAPI(values.rentalEndDate),
        },
        pickupLocation: {
          lat: parseFloat(values.pickupLat || values.lat),
          lon: parseFloat(values.pickupLon || values.lon),
          pickupLocationAddress: values.pickupLocation || values.address || '',
        },
        dropOffLocation: {
          lat: parseFloat(values.dropoffLat || values.lat),
          lon: parseFloat(values.dropoffLon || values.lon),
          dropOffLocationAddress: values.dropoffLocation || values.address || '',
        },
      }

      const response = await apiPost('deliveryy', requestData)

      if (response.success || response.code === 200) {
        notify.success(t('bookingProfile.messages.deliverySaved'))
        setDeliveryCreated(true)
        setDeliveryData(response.data)
        return true
      } else {
        notify.error(response.message || t('bookingProfile.messages.deliveryFailed'))
        return false
      }
    } catch (error) {
      notify.error(t('bookingProfile.messages.deliveryError'))
      return false
    } finally {
      setIsSubmitting(false)
    }
  }
  const fetchBookingToken = async bookingId => {
    if (!bookingId) return null

    try {
      const response = await apiPost(`booking/${bookingId}/token`, { createBookingToken: true })
      return response?.data?.token || response?.token || null
    } catch (error) {
      console.error('Error creating booking token:', error)
      return null
    }
  }

  const touchTransferFieldsForRetry = () => {
    const f = formikRef.current
    if (!f) return
    f.setFieldTouched('transferReferenceNumber', true, false)
    f.setFieldTouched('transferReceiptFileName', true, false)
  }

  const createBooking = async values => {
    try {
      setIsSubmitting(true)
      let transferReceiptUrl = ''

      if (values.paymentMethod === 'transfer') {
        if (!transferReceiptFile) {
          touchTransferFieldsForRetry()
          notify.error(t('bookingProfile.validation.transferReceiptRequired'))
          setIsSubmitting(false)
          return false
        }

        const okMime = _.isString(transferReceiptFile.type) && (_.startsWith(transferReceiptFile.type, 'image/') || transferReceiptFile.type === 'application/pdf')
        if (!okMime) {
          touchTransferFieldsForRetry()
          notify.error(t('bookingProfile.messages.receiptInvalidType'))
          setIsSubmitting(false)
          return false
        }

        const maxBytes = 15 * 1024 * 1024
        if (transferReceiptFile.size > maxBytes) {
          touchTransferFieldsForRetry()
          notify.error(t('bookingProfile.messages.receiptTooLarge'))
          setIsSubmitting(false)
          return false
        }

        const uploadForm = new FormData()
        uploadForm.append('file', transferReceiptFile)
        const uploadRes = await apiPost('booking/transfer-receipt', uploadForm, 0, false)
        const uploadedUrl = uploadRes?.data?.url
        if (!uploadRes?.success || !uploadedUrl) {
          formikRef.current?.setFieldTouched('transferReceiptFileName', true, false)
          notify.error(uploadRes?.message || t('bookingProfile.messages.receiptUploadFailed'))
          setIsSubmitting(false)
          return false
        }
        transferReceiptUrl = uploadedUrl
      }

      const userIdToUse = phoneUserId || values.selectedUser
      if (!userIdToUse) {
        notify.error(t('bookingProfile.messages.selectUserOrPhone'))
        setIsSubmitting(false)
        return false
      }

      const requestData = {
        rentalCarId: values.selectedCar,
        userId: userIdToUse,
        paymentMethod: values.paymentMethod,
        booking: {
          from: formatDateForAPI(values.rentalStartDate),
          to: formatDateForAPI(values.rentalEndDate),
        },
        location: {
          lat: parseFloat(values.lat),
          lon: parseFloat(values.lon),
        },
        isInsuranceRequired: values.insurance || false,
        extras: values.extras || [],
        couponCode: values.promoCode || '',
        deliveryId: deliveryData?.id || null,
        isEmailSent: isCompanyBookingFlow ? false : values.sentPaymentEmail || false,
        ...(isCompanyBookingFlow && values.phone ? { phone: normalizePhone(values.phone) } : {}),
        ...(isCompanyBookingFlow
          ? { paymentStatus: values.paymentStatus }
          : values.paymentMethod === 'direct_link'
          ? { status: values.status, paymentStatus: values.paymentStatus }
          : {}),
        ...(values.paymentMethod === 'transfer' && {
          transferReferenceNumber: _.trim(values.transferReferenceNumber || ''),
          transferReceiptUrl,
        }),
      }

      const response = await apiPost('create/booking', requestData)

      if (response.success || response.code === 200) {
        const { checkoutUrl, isPaymentRequired, id, bookingPaymentUrl: paymentLinkFromApi } = response.data

        setPaymentStatus(isPaymentRequired ? 'required' : 'completed')
        setTransferReceiptFile(null)
        formikRef.current?.setFieldValue('transferReceiptFileName', '')

        setBookingModelYear(selectedCar?.modelYear)
        setBookingMake(selectedCar?.car?.make)
        setBookingModel(selectedCar?.car?.model)
        setBookingId(id)

        if (isCompanyBookingFlow) {
          setBookingPaymentLink(null)
          setBookingToken(null)
          setPaymentUrl(null)
          setShouldRedirectAfterModal(false)
        } else {
          setBookingPaymentLink(paymentLinkFromApi || null)

          if (paymentLinkFromApi) {
            setBookingToken(null)
          } else if (isPaymentRequired) {
            const token = await fetchBookingToken(id)
            setBookingToken(token)
          } else {
            setBookingToken(null)
          }

          if (isPaymentRequired && checkoutUrl) {
            setPaymentUrl(checkoutUrl)
            setShouldRedirectAfterModal(true)
          } else {
            setPaymentUrl(null)
            setShouldRedirectAfterModal(false)
          }
        }

        openBookingSuccessModal()

        return true
      } else {
        notify.error(response.message || t('bookingProfile.messages.createFailed'))
        return false
      }
    } catch (error) {
      notify.error(t('bookingProfile.messages.createError'))
      return false
    } finally {
      setIsSubmitting(false)
    }
  }

  const formatDate = dateString => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    })
  }

  const calculateRentalDays = (startDate, endDate) => {
    if (!startDate || !endDate) return 0
    const start = new Date(startDate)
    const end = new Date(endDate)
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0
    const days = getDifferenceInDays(start, end)
    return Number.isNaN(days) ? 0 : days
  }

  const [LocationModal, openLocationModal, closeLocationModal] = useModal({
    content: (
      <LocationPicker
        onLocationSelect={location => {
          formikRef.current?.setFieldValue('lat', location.lat)
          formikRef.current?.setFieldValue('lon', location.lon)
          formikRef.current?.setFieldValue('address', location.address)
          closeLocationModal()
        }}
        onClose={() => closeLocationModal()}
        initialLat={formikRef.current?.values?.lat ? parseFloat(formikRef.current.values.lat) : undefined}
        initialLon={formikRef.current?.values?.lon ? parseFloat(formikRef.current.values.lon) : undefined}
      />
    ),
    title: t('bookingProfile.modals.selectLocation'),
    dimensions: {
      height: '550px',
      width: '650px',
    },
  })

  const [PickupLocationModal, openPickupLocationModal, closePickupLocationModal] = useModal({
    content: (
      <LocationPicker
        onLocationSelect={location => {
          formikRef.current?.setFieldValue('pickupLat', location.lat)
          formikRef.current?.setFieldValue('pickupLon', location.lon)
          formikRef.current?.setFieldValue('pickupLocation', location.address)
          if (formikRef.current?.values?.deliveryMethod === 'deliver') {
            formikRef.current?.setFieldValue('lat', location.lat)
            formikRef.current?.setFieldValue('lon', location.lon)
            formikRef.current?.setFieldValue('address', location.address)
          }
          closePickupLocationModal()
        }}
        onClose={() => closePickupLocationModal()}
        initialLat={
          formikRef.current?.values?.pickupLat || formikRef.current?.values?.lat ? parseFloat(formikRef.current.values.pickupLat || formikRef.current.values.lat) : undefined
        }
        initialLon={
          formikRef.current?.values?.pickupLon || formikRef.current?.values?.lon ? parseFloat(formikRef.current.values.pickupLon || formikRef.current.values.lon) : undefined
        }
      />
    ),
    title: t('bookingProfile.modals.selectPickupLocation'),
    dimensions: {
      height: '550px',
      width: '650px',
    },
  })

  const [DropoffLocationModal, openDropoffLocationModal, closeDropoffLocationModal] = useModal({
    content: (
      <LocationPicker
        onLocationSelect={location => {
          formikRef.current?.setFieldValue('dropoffLat', location.lat)
          formikRef.current?.setFieldValue('dropoffLon', location.lon)
          formikRef.current?.setFieldValue('dropoffLocation', location.address)
          closeDropoffLocationModal()
        }}
        onClose={() => closeDropoffLocationModal()}
        initialLat={
          formikRef.current?.values?.dropoffLat || formikRef.current?.values?.lat ? parseFloat(formikRef.current.values.dropoffLat || formikRef.current.values.lat) : undefined
        }
        initialLon={
          formikRef.current?.values?.dropoffLon || formikRef.current?.values?.lon ? parseFloat(formikRef.current.values.dropoffLon || formikRef.current.values.lon) : undefined
        }
      />
    ),
    title: t('bookingProfile.modals.selectDropoffLocation'),
    dimensions: {
      height: '550px',
      width: '650px',
    },
  })

  const BookingSuccessModalWrapper = ({ close }) => {
    const handleClose = () => {
      close()
      if (shouldRedirectAfterModal) {
        history.push('/bookings')
      } else if (isCompanyBookingFlow) {
        history.push('/company-dashboard')
      }
    }
    // Determine base URL based on URL parameters
    const getBaseUrl = () => {
      const pathname = location.pathname
      const searchParams = new URLSearchParams(location.search)

      // Check if URL contains "dashboard-dev"
      if (_.includes(pathname, 'dashboard-dev') || searchParams.has('dashboard-dev')) {
        return 'https://uat.ajarlee.om'
      }

      // Check if URL contains "dashboard" (but not "dashboard-dev")
      if (_.includes(pathname, 'dashboard') || searchParams.has('dashboard')) {
        return 'https://www.ajarlee.om'
      }

      // Otherwise, use the environment variable
      return process.env.REACT_APP_FRONTEND_HOST_URL || ''
    }

    const getBookingUrl = () => {
      if (isCompanyBookingFlow) return null
      if (bookingPaymentLink) return bookingPaymentLink
      if (!bookingId) return null

      let bookingUrl = `${getBaseUrl()}/booking/direct/${bookingId}`

      if (bookingToken) {
        const separator = _.includes(bookingUrl, '?') ? '&' : '?'
        bookingUrl = `${bookingUrl}${separator}token=${bookingToken}`
      }

      return bookingUrl
    }

    const bookingUrl = getBookingUrl()
    return (
      <BookingSuccessModal
        close={handleClose}
        modelYear={bookingModelYear || selectedCar?.modelYear}
        make={bookingMake || selectedCar?.car?.make}
        model={bookingModel || selectedCar?.car?.model}
        bookingUrl={bookingUrl}
      />
    )
  }

  const [BookingSuccessModalComponent, openBookingSuccessModal] = useModal({
    content: <BookingSuccessModalWrapper />,
    title: t('bookingProfile.modals.bookingCreated'),
    closeable: true,
    dimensions: {
      height: 'auto',
      width: '90vw',
      maxWidth: '500px',
      minHeight: 'auto',
      minWidth: '280px',
    },
  })

  return (
    <Layout
      background="#f4f5f9"
      title={t('bookingProfile.title')}
      breadcrumbs={
        isCompanyBookingFlow
          ? [
              { name: t('bookingProfile.breadcrumbs.companyDashboard'), pathname: '/company-dashboard' },
              { name: t('bookingProfile.breadcrumbs.create'), pathname: '/create/booking', current: true },
            ]
          : [
              { name: t('bookingProfile.breadcrumbs.bookings'), pathname: '/bookings' },
              { name: t('bookingProfile.breadcrumbs.create'), pathname: '/create/booking', current: true },
            ]
      }
      headerRight={
        <button type="button" className={BOOKING_BTN_GHOST} onClick={() => history.push(isCompanyBookingFlow ? '/company-dashboard' : '/bookings')}>
          {isCompanyBookingFlow ? t('bookingProfile.buttons.goBackCompany') : t('bookingProfile.buttons.goBack')}
        </button>
      }
    >
      <Formik
        innerRef={ref => {
          formikRef.current = ref
        }}
        enableReinitialize
        validateOnChange={false}
        validateOnBlur
        validateOnMount={false}
        initialValues={bookingInitialValues}
        validationSchema={validationSchema}
      >
        {({ values, setFieldValue, errors, touched, validateForm, setFieldTouched, setTouched }) => {
          const steps = getSteps()
          const lastStepId = steps[steps.length - 1]?.id
          const isLastStep = activeStep === lastStepId
          const isDetailStep = activeStep === 3

          const phoneRequired = isCompanyBookingFlow || !values.selectedUser
          const isDeliver = values.deliveryMethod === 'deliver'
          const hasDeliverLocations = isDeliver && values.pickupLat && values.pickupLon && values.dropoffLat && values.dropoffLon
          const hasPickupLocation = !isDeliver && values.lat && values.lon
          const hasLocationForStep1 = hasDeliverLocations || hasPickupLocation

          const nextDisabled =
            (activeStep === 1 &&
              (!values.deliveryMethod ||
                !hasLocationForStep1 ||
                !values.rentalStartDate ||
                !values.rentalEndDate ||
                errors.rentalStartDate ||
                errors.rentalEndDate ||
                errors.phone ||
                errors.deliveryMethod ||
                errors.lat ||
                errors.pickupLat ||
                errors.dropoffLat ||
                (phoneRequired && (!values.phone || !isValidBookingPhone(values.phone))))) ||
            (activeStep === 2 && !values.selectedCar)

          return !loading ? (
            <div className="relative flex min-h-[50vh] w-full flex-1 overflow-auto bg-slate-50/40">
              <div className="mx-auto w-full max-w-[1600px]">
                <div className={BOOKING_PAGE_CANVAS}>
                  <div className={BOOKING_PANEL}>
                    <Form className="space-y-6">
                      <div className="mb-2 border-b border-slate-200/80 pb-6">
                        {readOnly && <p className="mb-2 text-sm font-medium text-amber-700">{t('bookingProfile.readOnlySuffix')}</p>}
                        <CreateBookingStepper steps={steps} activeStep={activeStep} />
                      </div>

                      {activeStep === 1 && (
                        <LocationStep
                          t={t}
                          values={values}
                          errors={errors}
                          touched={touched}
                          setFieldValue={setFieldValue}
                          formikRef={formikRef}
                          hasAdminAccess={hasAdminAccess}
                          isCompanyBookingFlow={isCompanyBookingFlow}
                          userOptions={userOptions}
                          fetchUsers={fetchUsers}
                          isArabic={isArabic}
                          datePickerLocale={datePickerLocale}
                          selectDateTimePlaceholder={selectDateTimePlaceholder}
                          clickToPickLocationPlaceholder={clickToPickLocationPlaceholder}
                          clickToPickPickupPlaceholder={clickToPickPickupPlaceholder}
                          clickToPickDropoffPlaceholder={clickToPickDropoffPlaceholder}
                          openLocationModal={openLocationModal}
                          openPickupLocationModal={openPickupLocationModal}
                          openDropoffLocationModal={openDropoffLocationModal}
                          onDeliveryMethodChange={handleDeliveryMethodChange}
                        />
                      )}

                      {activeStep === 2 && (
                        <CarsStep
                          t={t}
                          values={values}
                          search={search}
                          handleSearchChange={handleSearchChange}
                          carData={carData}
                          isLoadingCars={isLoadingCars}
                          loadMore={loadMore}
                          pagination={pagination}
                          selectedCarId={selectedCarId}
                          loadingCarId={loadingCarId}
                          handleSelectCar={handleSelectCar}
                          setFieldValue={setFieldValue}
                          searchOptionsPlaceholder={searchOptionsPlaceholder}
                          isRTL={isArabic}
                          isCompanyBookingFlow={isCompanyBookingFlow}
                          showCompanyBranchFilter={isCompanyBookingFlow && isSuperCompanyUser}
                          companyBranchOptions={companyBranchOptions}
                          branchesLoading={branchesLoading}
                          onBranchChange={handleCompanyBranchChange}
                        />
                      )}

                      {isDetailStep && (
                        <ReviewStep
                          t={t}
                          values={values}
                          errors={errors}
                          touched={touched}
                          setFieldValue={setFieldValue}
                          setFieldTouched={setFieldTouched}
                          setTouched={setTouched}
                          validateForm={validateForm}
                          carDetail={carDetail}
                          selectedCar={selectedCar}
                          deliveryData={deliveryData}
                          isDeliveryPricingLoading={isDeliveryPricingLoading}
                          pricingError={pricingError}
                          formatDate={formatDate}
                          calculateRentalDays={calculateRentalDays}
                          transferReceiptFile={transferReceiptFile}
                          setTransferReceiptFile={setTransferReceiptFile}
                          isSubmitting={isSubmitting}
                          createBooking={createBooking}
                          onPrevious={handlePrevious}
                          isCompanyBookingFlow={isCompanyBookingFlow}
                        />
                      )}

                      {!isLastStep && (
                        <CreateBookingWizardFooter
                          t={t}
                          isLastStep={isLastStep}
                          showPrevious={activeStep > 1}
                          onPrevious={handlePrevious}
                          onNext={() => handleNext(values)}
                          nextDisabled={nextDisabled}
                          showNext={activeStep !== 2}
                          isLoading={isResolvingPhone || (activeStep === 3 && (isDeliveryPricingLoading || isSubmitting))}
                        />
                      )}
                    </Form>
                  </div>
                </div>
              </div>
              <LocationModal />
              <PickupLocationModal />
              <DropoffLocationModal />
              <BookingSuccessModalComponent />
            </div>
          ) : (
            <div className="flex min-h-[min(70vh,560px)] w-full flex-col items-center justify-center gap-4 px-4 py-16">
              <LoadingIcon />
              <p className="text-sm font-medium text-slate-600">{t('common.loading')}</p>
            </div>
          )
        }}
      </Formik>
    </Layout>
  )
}

export default BookingProfile
